import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Gift, Bookmark } from "lucide-react";
import { useAuth } from "../helpers/useAuth";
import { useLicenseStatus } from "../helpers/useLicenseStatus";
import { useTirageAnalyse } from "../helpers/useTirageAnalyse";
import { getMyDrawTonight } from "../endpoints/auth/my-draw-tonight_GET.schema";
import { getLandingStats } from "../endpoints/public/landing-stats_GET.schema";
import { getRetrospectiveGains } from "../endpoints/auth/my-draw-retrospective-gains_GET.schema";
import { getReferralCode } from "../endpoints/referral/code_GET.schema";
import { limitsFor } from "../helpers/entitlements";
import { NAV_ITEMS, NAV_RUBRIQUES, type NavItem } from "../helpers/navCatalog.generated";
import { PRICING } from "../helpers/kenoEfficacite";
import { FENETRE, datesCourtes, faitsAccueil, nomSalutation, numerosEnCeMoment, prochainTirage, resumeMonKeno } from "../helpers/tableauDeBord";
import { etatEssai, texteEssai } from "../helpers/essai";
import { BandeauEssai } from "./grilles/BandeauEssai";
import { SEUIL_BANDEAU_ESSAI } from "./site/EtatBandeaux";
import { PremiereGrille } from "./grilles/PremiereGrille";
import { MesFavoris } from "./MesFavoris";
import { useFavoris } from "../helpers/useFavoris";
import styles from "./DashboardView.module.css";
import { useNumeroDetailSheet } from "../helpers/useNumeroDetailSheet";

/**
 * Tableau de bord (espace connecté, lot 2, décision D3 du 19/10/2026) : une page fixe qui
 * répond dans l'ordre à « où en sont mes numéros », « que s'est-il passé hier », « que
 * regarder ce soir ». Maquette : docs/superpowers/specs/2026-10-16-espace-connecte/index.html.
 * Tous les nombres viennent du serveur (API, quotas, catalogue, PRICING) : aucun recopié.
 */

type Liste = { numero: number; ecart: number; score?: number; ecartMax?: number; percent?: number; broken?: boolean };
type Listes = { lists: { synthese?: Liste[]; records?: Liste[] }; meta: { totalTirages?: number } };

const euros = (v: number) => `${Number.isInteger(v) ? v : v.toFixed(2).replace(".", ",")} €`;
const decimal = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1).replace(".", ","));
const jourLong = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
const majuscule = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Étiquette des faits marquants : mêmes libellés et tons que la page d'un tirage
 * (src/Helper/HighlightTone.php), sauf le bleu, rendu neutre : il reste réservé aux accès.
 */
const TAGS: Record<string, [string, string]> = {
  serie: ["Série", "red"], series: ["Série", "red"], streak_follower: ["Série", "red"],
  twins: ["Jumeaux", "green"], twin_draw: ["Jumeaux", "green"],
  absence: ["Absence", "neutral"], absences: ["Absence", "neutral"],
  comeback: ["Retour", "gold"], comebacks: ["Retour", "gold"],
  annonceur: ["Annonciateur", "gold"],
  rhythm: ["Rythme", "gold"],
  mirror: ["Miroir", "green"],
  suite: ["Suite", "neutral"], neighbors: ["Suite", "neutral"],
  multiples: ["Multiples", "gold"],
  hot_zone: ["Zone chaude", "red"],
  dizaine_absente: ["Dizaine absente", "neutral"],
  finale_absente: ["Finale absente", "neutral"],
  finale_dominante: ["Finale dominante", "gold"],
  rien_de_la_veille: ["Rien de la veille", "neutral"],
  double_digits: ["Doubles", "neutral"],
  fibonacci: ["Fibonacci", "gold"],
  somme_palindrome: ["Somme palindrome", "neutral"],
  suite_vide: ["Suite vide", "neutral"],
  tirage_bas: ["Tirage bas", "neutral"],
  premiers: ["Nombres premiers", "gold"],
  numero_du_mois: ["Numéro du mois", "gold"],
  jour_de_fete: ["Jour de fête", "gold"],
  date_du_jour: ["Date du jour", "neutral"],
  coincidence: ["Coïncidence", "neutral"],
};

/** Boule du tableau de bord : ouvre la fiche rapide du numéro sur le côté (comme partout ailleurs sur le site). */
const Boule = ({ n, sortie, mienne, petite }: { n: number; sortie?: boolean; mienne?: boolean; petite?: boolean }) => {
  const { openSheet } = useNumeroDetailSheet();
  return (
    <button type="button" className={[styles.b, styles.bBtn, sortie ? styles.bOut : "", mienne ? styles.bMine : "", petite ? styles.bSm : ""].join(" ")} onClick={() => openSheet(n)} aria-label={`Numéro ${n} : voir sa fiche rapide`}>{n}</button>
  );
};

const outil = (label: string): NavItem | undefined => NAV_ITEMS.find((i) => i.label === label);

export const DashboardView: React.FC = () => {
  const { authState } = useAuth();
  const { data: licence } = useLicenseStatus();
  const { data: favoris } = useFavoris();
  const epingles = new Set(favoris?.favoris.map((f) => f.path) ?? []);
  const premium = licence?.hasFullAccess === true;
  // Essai Premium en cours (aperçu ou essai de 14 jours) : bandeau seulement ; pour les étiquettes,
  // l'essai est traité comme Premium (décision du 23/10/2026, cadrage §7 point 12).
  const essai = etatEssai(licence);
  // Les 3 derniers jours, le bandeau de fin d'essai (EtatBandeaux) prend le relais : un seul bandeau.
  const finEssaiProche = (licence?.trialDaysRemaining ?? Infinity) <= SEUIL_BANDEAU_ESSAI;
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const bienvenue = searchParams.get("bienvenue") === "1";
  // Premier passage (/home/?bienvenue=1, aucune grille) : le bloc reste affiché jusqu'au départ
  // de la page, même après l'enregistrement, pour la confirmation et le choix du mail.
  const [premierPassage, setPremierPassage] = useState(false);
  const gratuit = limitsFor("free");
  const pro = limitsFor("premium");

  const { data: monKeno } = useQuery({ queryKey: ["my-draw-tonight"], queryFn: getMyDrawTonight, staleTime: 5 * 60_000 });
  const { data: accueil } = useQuery({ queryKey: ["landing-stats"], queryFn: () => getLandingStats(), staleTime: 10 * 60_000 });
  const dernier = accueil?.lastTirage?.date?.slice(0, 10) ?? "";
  const { data: tirage } = useTirageAnalyse(dernier);
  const { data: listes } = useQuery<Listes>({
    queryKey: ["pronostic-lists"],
    queryFn: async () => (await fetch("/_api/auth/pronostic-lists", { credentials: "same-origin" })).json(),
    staleTime: 10 * 60_000,
  });
  const { data: retro } = useQuery({ queryKey: ["my-draw-retrospective-gains"], queryFn: () => getRetrospectiveGains(), enabled: licence !== undefined && !premium, staleTime: 10 * 60_000 });
  const { data: parrainage } = useQuery({ queryKey: ["referral-code"], queryFn: () => getReferralCode(), staleTime: 60 * 60_000 });

  useEffect(() => {
    if (bienvenue && monKeno && !monKeno.configured) setPremierPassage(true);
  }, [bienvenue, monKeno]);

  const grilleEnregistree = () => {
    void queryClient.invalidateQueries({ queryKey: ["my-draw-tonight"] });
    void queryClient.invalidateQueries({ queryKey: ["favorite-combinations"] });
    // Un rechargement de la page ne doit pas rouvrir le premier écran.
    const p = new URLSearchParams(searchParams);
    p.delete("bienvenue");
    setSearchParams(p, { replace: true });
  };

  if (authState.type !== "authenticated") return null;
  const user = authState.user;
  const nom = nomSalutation(user.displayName, user.email);
  const prochain = prochainTirage(dernier);
  const grille = monKeno?.configured ? monKeno.playerNumbers ?? [] : [];
  const historique = monKeno?.history ?? [];
  const resume = resumeMonKeno(historique);
  const enCeMoment = numerosEnCeMoment(grille, historique);
  const maxBarre = Math.max(0, ...resume.barres.map((b) => b.score));
  const total = listes?.meta?.totalTirages ?? accueil?.totalTirages ?? null;
  const faits = [...(tirage?.highlights ?? [])].sort((a, b) => b.priority - a.priority);
  const outilsRubrique = NAV_RUBRIQUES.find((r) => r.key === "outils")?.items.length ?? null;
  const records = listes?.lists.records ?? [];
  // Aucun numéro déjà affiché dans les cartes du soir (top 3 de La Synthèse, 3 records) ne revient dans les faits.
  const faitsVus = faitsAccueil(faits, [...(listes?.lists.synthese ?? []).slice(0, 3).map((x) => x.numero), ...records.slice(0, 3).map((r) => r.numero)]);
  const sansGrillePremierPassage = premierPassage && grille.length === 0;

  // Violet Premium toujours avec le mot « Premium ». Abonné et essai (décisions du 22 et du
  // 23/10/2026) : aucune étiquette d'accès, seulement le contenu.
  const acces = (item: NavItem | undefined, quota?: [number, number, string], compte = false) => {
    if (premium) return null;
    // « Jusqu’à 3 grilles » : se lit seul à 375, où la description est masquée. Une limite, pas un droit :
    // pastille neutre, le vert reste à « Compte gratuit ».
    if (quota) return <span className={styles.accQuota}>{`Jusqu’à ${quota[0]} ${quota[2]}`}</span>;
    if (item?.tier === "premium") return <span className={styles.accPre}>Premium</span>;
    if (item?.tier === "account" || compte) return <span className={styles.accAcc}>Compte gratuit</span>;
    return <span className={styles.accLibre}>Libre</span>;
  };
  // Descriptions écrites pour le connecté : celles du catalogue s'adressent au visiteur anonyme.
  // Compte gratuit : la pastille porte la limite, la description dit ce que fait l'outil (pas de redite).
  const outils: { label: string; quota?: [number, number, string]; desc?: string; compte?: boolean }[] = [
    { label: "Générateur de grilles", quota: [gratuit.generatorMaxGrids, pro.generatorMaxGrids, "grilles"], desc: premium ? `Jusqu’à ${pro.generatorMaxGrids} grilles par génération` : "Grilles construites à partir de vos critères" },
    { label: "Tester ma grille" },
    { label: "Comparateur", quota: [gratuit.comparatorMaxNumbers, pro.comparatorMaxNumbers, "numéros"], desc: premium ? `Jusqu’à ${pro.comparatorMaxNumbers} numéros côte à côte` : "Sorties, écarts et tendance récente côte à côte" },
    { label: "Relations" },
    { label: "Système réducteur" },
    // Page publique (classements), participation avec un compte, identique en gratuit et en Premium.
    // Description du catalogue, neutre pour tous (décision du 23/10/2026) : l'accès est porté par l'étiquette.
    { label: "Labo de simulation", compte: true },
  ];

  return (
    <div className={styles.page}>
      {premierPassage ? (
        <PremiereGrille
          essai={essai}
          prochain={prochain}
          notification={monKeno?.configured ? monKeno.myDrawNotification : "none"}
          mailRefuse={monKeno?.mailResultatRefuse === true}
          onEnregistree={grilleEnregistree}
          onChoisi={() => void queryClient.invalidateQueries({ queryKey: ["my-draw-tonight"] })}
        />
      ) : (
        <section className={styles.greet} aria-labelledby="h-home">
          <h1 id="h-home">{nom ? `Bonjour ${nom}` : "Bonjour"}</h1>
          <p>
            {majuscule(new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }))}
            {` · prochain tirage ${prochain} à 20 h`}
            {dernier && ` · dernier résultat : ${jourLong(dernier)}`}
          </p>
          {essai && !finEssaiProche && (
            <div className={styles.essai}>
            <BandeauEssai
              texte={texteEssai(essai)}
              lien={{ href: "/tarifs/?cta_source=essai_bandeau#comparatif", label: "Ce que garde le compte gratuit" }}
              renderLien={(href, label, className) => <Link className={className} to={href}>{label}</Link>}
            />
            </div>
          )}
        </section>
      )}

      <section className={styles.tonight} aria-label="Ce soir">
        {/* Premier passage sans grille : le bloc du haut porte l'action et la promesse du score. */}
        {!sansGrillePremierPassage && <article className={styles.mk} aria-labelledby="h-mk">
          <header><h2 id="h-mk">Mes grilles</h2><Link className={styles.tlink} to="/mon-keno/">Ouvrir Mes grilles</Link></header>
          {grille.length === 0 ? (
            <div className={styles.vide}>
              <p>Choisissez vos numéros une fois : vous retrouverez ici votre score à chaque tirage.</p>
              <Link className="km-btn" to="/mon-keno/">Choisir mes numéros</Link>
            </div>
          ) : (
            <>
              <p className={styles.score}>
                <strong>{monKeno?.matchCount ?? 0}<small> / {grille.length}</small></strong>
                <span>
                  {(monKeno?.matchCount ?? 0) > 1 ? "numéros trouvés" : "numéro trouvé"}
                  {monKeno?.tirageDate && ` au tirage du ${jourLong(monKeno.tirageDate)}`}
                  {(monKeno?.matched?.length ?? 0) > 0 && <> : {monKeno!.matched!.map((n, i) => <React.Fragment key={n}>{i > 0 && ", "}<b>{n}</b></React.Fragment>)}</>}
                </span>
              </p>
              <div className={styles.balls} aria-label="Vos numéros">{grille.map((n) => <Boule key={n} n={n} sortie={monKeno?.matched?.includes(n)} petite />)}</div>
              {resume.barres.length > 1 && (
                <>
                  {/* Échelle de 0 à la taille de la grille : la ligne du haut est le maximum, la plus haute barre porte sa valeur. */}
                  <ol className={styles.mybar} aria-label={`Bons numéros sur vos ${resume.barres.length} derniers tirages, sur ${grille.length} au maximum`} data-max={grille.length}>
                    {resume.barres.map((b) => <li key={b.date} style={{ height: `${(b.score / grille.length) * 100}%` }} data-zero={b.score === 0 || undefined} data-v={maxBarre > 0 && b.score === maxBarre ? b.score : undefined} title={`${b.date.slice(8, 10)}/${b.date.slice(5, 7)} : ${b.score} sur ${grille.length}`} />)}
                  </ol>
                  <p className={styles.mybarL}><span>{datesCourtes([resume.barres[0].date])}</span><span>vos {resume.barres.length} derniers tirages</span><span>{datesCourtes([resume.barres.at(-1)!.date])}</span></p>
                </>
              )}
              <dl className={styles.kpis}>
                {resume.moyenne !== null && <div><dt>Moyenne</dt><dd>{decimal(resume.moyenne)} <small>sur {resume.nombreTirages} tirages</small></dd></div>}
                {/* Plus de 3 dates : le nombre et la plus récente, jamais une liste tronquée qui se lirait complète. */}
                {resume.meilleur && <div><dt>Meilleur</dt><dd>{resume.meilleur.score} / {grille.length} <small>{resume.meilleur.dates.length > 3 ? `${resume.meilleur.dates.length} fois, dernière le ${datesCourtes([[...resume.meilleur.dates].sort().at(-1)!])}` : datesCourtes(resume.meilleur.dates)}</small></dd></div>}
                {/* Le prochain tirage est dans le sous-titre de l'en-tête : pas de troisième indicateur. */}
              </dl>
            </>
          )}
        </article>}

        {tirage && (
          <article className={styles.res} aria-labelledby="h-draw">
            <div className={styles.top}>
              <span className={styles.rdate} id="h-draw">{majuscule(jourLong(tirage.tirage.date))} · {tirage.tirage.numeros.length} numéros</span>
              {tirage.tirage.multiplicateur && <span className={styles.mult}>Multiplicateur ×{tirage.tirage.multiplicateur}</span>}
            </div>
            <div className={styles.tballs}>{tirage.tirage.numeros.map((n) => <Boule key={n} n={n} sortie mienne={grille.includes(n)} />)}</div>
            {/* « Sorti » ne sert qu'à distinguer « Dans vos numéros » : sans grille, les 16 boules sont toutes sorties. */}
            {(grille.length > 0 || tirage.tirage.numeroJokerplus) && (
              <div className={styles.legend}>
                {grille.length > 0 && <span><i className={`${styles.b} ${styles.bOut}`} />Sorti</span>}
                {grille.length > 0 && <span><i className={`${styles.b} ${styles.bOut} ${styles.bMine}`} />Dans vos numéros</span>}
                {tirage.tirage.numeroJokerplus && <span className={styles.joker}>Joker+ <strong>{tirage.tirage.numeroJokerplus}</strong></span>}
              </div>
            )}
            {faits.length > 0 && (
              <ul className={styles.facts} aria-label="Faits marquants">
                {faitsVus.map((f) => {
                  const [label, ton] = TAGS[f.type] ?? ["Fait", "gold"];
                  return <li key={f.text}><span className={`${styles.tag} ${styles[`tag_${ton}`]}`}>{label}</span><span>{f.text}</span></li>;
                })}
              </ul>
            )}
            <div className={styles.acts}>
              <a className="km-btn km-btn--ghost" href={`/tirage/${tirage.tirage.date.slice(0, 10)}/`}>Analyse du tirage <ArrowRight aria-hidden="true" /></a>
              {faits.length > 3 && <a className={styles.tlink} href={`/tirage/${tirage.tirage.date.slice(0, 10)}/#t-faits`}>Les {faits.length} faits marquants</a>}
            </div>
          </article>
        )}
      </section>

      <section className={styles.sec} aria-labelledby="h-now">
        <div className={styles.secH}>
          <h2 id="h-now">Pour le tirage de ce soir</h2>
          {/* Le nombre de tirages pour Premium seulement : le compte gratuit calcule sur ses 50 derniers (décision du 22/10/2026). */}
          {dernier && <span className={styles.sub}>Listes calculées après le tirage du {jourLong(dernier)}{premium && total !== null ? `, sur ${total} tirages` : ""}</span>}
        </div>
        <div className={styles.trio}>
          <article className={styles.card}>
            {/* La Synthèse est une liste publique : pas d'étiquette d'accès. Le score a son nom et sa méthode. */}
            <header><h3>La Synthèse · top 3</h3><a className={styles.colH} href="/pronostics-keno/#methode" aria-label="Indice de La Synthèse : comment il est calculé">Indice</a></header>
            <ul className={styles.mini}>
              {(listes?.lists.synthese ?? []).slice(0, 3).map((l) => (
                <li key={l.numero}><Boule n={l.numero} petite /><span>absent depuis {l.ecart} tirage{l.ecart > 1 ? "s" : ""}</span>{l.score !== undefined && <strong title={`Indice de La Synthèse : ${decimal(l.score)}`} aria-label={`indice ${decimal(l.score)}`}>{decimal(l.score)}</strong>}</li>
              ))}
            </ul>
            <a className={styles.more} href="/pronostics-keno/">Les listes du soir <ArrowRight aria-hidden="true" /></a>
          </article>
          {/* Premier passage sans grille : la carte ne ferait que répéter la promesse du bloc du haut. */}
          {!sansGrillePremierPassage && <article className={styles.card}>
            <header><h3>Vos numéros en ce moment</h3>{enCeMoment.fenetre > 0 && <span className={styles.sub}>sur {enCeMoment.fenetre} tirages</span>}</header>
            {grille.length === 0 ? (
              <p className={styles.note}>{`Dès votre première grille : votre numéro le plus présent, le plus absent, et combien sont sortis sur les ${FENETRE} derniers tirages.`}</p>
            ) : (
              <ul className={styles.mini}>
                {enCeMoment.plusPresent && <li><Boule n={enCeMoment.plusPresent.numero} sortie petite /><span>le plus présent de vos numéros</span><strong>{enCeMoment.plusPresent.sorties} sortie{enCeMoment.plusPresent.sorties > 1 ? "s" : ""}</strong></li>}
                {enCeMoment.plusAbsent && <li><Boule n={enCeMoment.plusAbsent.numero} petite /><span>le plus absent de vos numéros</span><strong>{enCeMoment.plusAbsent.auMoins ? "plus de " : ""}{enCeMoment.plusAbsent.absence} tirages</strong></li>}
                <li><span className={styles.cnt}>{enCeMoment.sortis}/{grille.length}</span><span>de vos numéros sortis au moins une fois</span></li>
              </ul>
            )}
            {grille.length > 0 ? (
              <Link className={styles.more} to="/mon-keno/">{`Vos ${grille.length} numéros dans Mes grilles`} <ArrowRight aria-hidden="true" /></Link>
            ) : (
              <Link className={`km-btn km-btn--ghost ${styles.cta}`} to="/mon-keno/">Choisir mes numéros</Link>
            )}
          </article>}
          <article className={styles.card}>
            <header><h3>Proches de leur record</h3><span className={styles.sub}>absence ≥ 85 % du record</span></header>
            {records.length === 0 ? (
              <p className={styles.note}>Aucun numéro n’approche son record d’absence ce soir.</p>
            ) : (
              <ul className={styles.mini}>
                {records.slice(0, 3).map((r) => (
                  <li key={r.numero}><Boule n={r.numero} petite /><span>{`${r.ecart}\u00a0tirages`}<span className={styles.long}> d’absence</span>{", "}{r.broken ? "record battu" : r.ecart === r.ecartMax ? "record égalé" : `record de\u00a0${r.ecartMax}`}</span><strong>{r.percent} %</strong></li>
                ))}
              </ul>
            )}
            {records[0] && <a className={styles.more} href={`/numero/${records[0].numero}/`}>Fiche du numéro {records[0].numero} <ArrowRight aria-hidden="true" /></a>}
          </article>
        </div>
      </section>

      <MesFavoris />

      <section className={styles.sec} aria-labelledby="h-tools">
        <div className={styles.secH}>
          <h2 id="h-tools">Vos outils</h2>
          <a className={styles.tlink} href="/outils-keno/">{outilsRubrique ? `Les ${outilsRubrique} outils` : "Tous les outils"}</a>
        </div>
        {/* Abonné : sans étiquette d'accès, la carte n'aurait qu'un titre à 375 ; data-pre la passe en liste titre et description. */}
        <nav className={styles.tools6} data-pre={premium || undefined} aria-label="Outils">
          {outils.map(({ label, quota, desc, compte }) => {
            const item = outil(label);
            if (!item) return null;
            // Déjà dans « Mes favoris » : la carte reste (grille stable), marquée d'un signet.
            const favori = epingles.has(item.path) ? <Bookmark className={styles.favori} aria-label="Dans vos favoris" role="img" /> : null;
            const contenu = <><strong>{item.label}{favori}</strong>{acces(item, quota, compte)}<span className={styles.d}>{desc ?? item.desc}</span></>;
            return item.isStatic ? <a key={label} href={item.href}>{contenu}</a> : <Link key={label} to={item.href}>{contenu}</Link>;
          })}
        </nav>
      </section>

      {licence && !premium && (
        <section className={styles.sec} aria-labelledby="h-prem">
          <div className={styles.prem}>
            <div>
              {/* Sans grille, « vos numéros » ne désigne encore rien. */}
              <h2 id="h-prem">{grille.length > 0 ? "Vos numéros sur tout l’historique" : "Tout l’historique avec Premium"}</h2>
              <p className={styles.premP}>
                Relations et comparateur calculent sur les {gratuit.tirageDepth} derniers tirages avec le compte gratuit.
                {total !== null && ` Premium ouvre les ${total} tirages depuis novembre 2025.`}
              </p>
              <ul>
                {/* Grille rejouée sur des tirages passés (avant le suivi de Mes grilles) : dit comme tel, pour ne pas
                    se lire comme un second « meilleur » contredisant celui de la carte Mes grilles. Constat, aucune promesse. */}
                {retro?.configured && retro.gains?.biggestGain && (
                  <li>
                    {retro.limitApplied ? `Rejoués sur les ${retro.limitApplied} derniers tirages` : "Rejoués sur l’historique"}, jusqu’à <b>{retro.gains.biggestGain.matchCount} de vos {retro.gains.gridSize} numéros</b> seraient sortis le même jour, le {jourLong(retro.gains.biggestGain.date)}.
                    {retro.limitApplied && " Premium les rejoue sur tout l’historique."}
                  </li>
                )}
                <li>Jusqu’à {pro.maxCombinations} grilles suivies et des alertes sur vos numéros.</li>
                <li>Analyse IA de chaque tirage, réducteur, comparateur à {pro.comparatorMaxNumbers} numéros.</li>
              </ul>
            </div>
            <div className={styles.go}>
              <p><strong>{euros(PRICING.annual.amount)}</strong> la première année, {euros(PRICING.renewal.amount)} si vous renouvelez</p>
              <p>ou {euros(PRICING.monthly.amount)} par mois, sans engagement</p>
              <Link className="km-btn" to="/tarifs/?cta_source=tableau_de_bord">Passer Premium</Link>
              <Link className={styles.tlink} to="/tarifs/?cta_source=tableau_de_bord#comparatif">Comparer les formules</Link>
            </div>
          </div>
        </section>
      )}

      {parrainage?.referralCode && (
        <p className={styles.refl}>
          <Gift aria-hidden="true" />
          <span>Parrainage : 30 jours de Premium pour vous à chaque ami qui s'abonne.</span>
          <code>{parrainage.referralCode}</code>
          <Link className={styles.tlink} to="/compte/">Mon lien</Link>
        </p>
      )}
    </div>
  );
};
